import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {checkHealth} from '../lib/api';

/**
 * 'checking' — the first probe has not resolved yet (treated as healthy for gating
 *              so we never flash a false outage on load).
 * 'ok'       — the last probe succeeded.
 * 'down'     — the last probe failed (network error or non-2xx).
 */
export type HealthStatus = 'checking' | 'ok' | 'down';

interface HealthContextValue {
  status: HealthStatus;
  /** True only when we affirmatively know the API is unreachable. */
  isDown: boolean;
  /** Force an immediate re-probe (e.g. from a "retry" button). */
  refresh: () => void;
}

const HealthContext = createContext<HealthContextValue | null>(null);

// Poll cadence. The endpoint is a cheap MOCK integration, so a tight-ish interval
// is fine; 30s keeps the banner responsive without hammering the edge.
const POLL_INTERVAL_MS = 30_000;

export function HealthProvider({children}: {children: React.ReactNode}): React.ReactElement {
  const [status, setStatus] = useState<HealthStatus>('checking');

  const probe = useCallback(async (signal?: AbortSignal) => {
    const ok = await checkHealth(signal);
    if (signal?.aborted) {
      return;
    }
    setStatus(ok ? 'ok' : 'down');
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void probe(controller.signal);

    const interval = setInterval(() => void probe(controller.signal), POLL_INTERVAL_MS);
    // Re-probe when the tab regains focus so a returning user sees fresh state.
    const onFocus = () => void probe(controller.signal);
    window.addEventListener('focus', onFocus);

    return () => {
      controller.abort();
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [probe]);

  const refresh = useCallback(() => void probe(), [probe]);

  return (
    <HealthContext.Provider value={{status, isDown: status === 'down', refresh}}>
      {children}
    </HealthContext.Provider>
  );
}

export function useHealth(): HealthContextValue {
  const ctx = useContext(HealthContext);
  if (!ctx) {
    throw new Error('useHealth must be used within HealthProvider');
  }
  return ctx;
}
